/**
 * Carbon Plugin 공통 코드 글로벌 객체
 * - API(/plugins/carbon/common-code)에서 로드한 데이터를 캐시
 * - Vuex 의존 없음, 단순 참조용
 *
 * 사용법:
 *   CarbonCodes.fuelType.getLabel(item.fuelType);
 *   CarbonCodes.fuelType.getList();
 *   CarbonCodes.facilityType.getLabel(item.facilityType);  // FIELD_TO_GROUP 미정의 시 코드값 그대로 반환
 *   CarbonCodes.getLabel("FUEL_TYPE", value);  // groupCode 직접 지정
 */

import Vue from "vue";
import carbonApi from "../api";

// Vue 반응성을 이용해 로드 완료 시점에 모든 컴포넌트 렌더가 재평가되도록 하는 플래그
// (CarbonCodes는 Vuex 의존이 없지만, 이 플래그만큼은 Vue가 추적할 수 있어야 함)
const reactiveState = Vue.observable({
    loaded: false,
});

/** FIELD_TO_GROUP에 없는 필드 접근 시 코드값 그대로 반환하는 패스스루 헬퍼 */
const passThroughHelper = {
    getLabel: (value) => value ?? "-",
    getList: () => [],
    getLabelEn: (value) => value ?? "-",
    getMeta: () => undefined,
};

/** API 응답 필드명(camelCase) → DB groupCode */
const FIELD_TO_GROUP = {
    status: "STATUS",
    draftStat: "DRAFT_STAT",
    goalStat: "GOAL_STAT",
    periodType: "PERIOD_TYPE",
    accountingMethod: "ACCOUNTING_METHOD",
    scopeType: "SCOPE_TYPE",
    streamType: "STREAM_TYPE",
    collectionMethod: "COLLECTION_METHOD",
    dataSource: "DATA_SOURCE",
    factorMethodology: "FACTOR_METHODOLOGY",
    evidenceCategory: "EVIDENCE_CATEGORY",
    evdTypeActivity: "EVD_TYPE_ACTIVITY",
    evdTypeFactor: "EVD_TYPE_FACTOR",
    inventoryStat: "INVENTORY_STAT",
    evdTypeReport: "EVD_TYPE_REPORT",
    reportCycle: "REPORT_CYCLE",
    reportPeriodUnit: "REPORT_PERIOD_UNIT",
    collectCycle: "COLLECT_CYCLE",
    evdTypeOrg: "EVD_TYPE_ORG",
    evdTypeFacility: "EVD_TYPE_FACILITY",
    evdTypeCompliance: "EVD_TYPE_COMPLIANCE",
    factorType: "FACTOR_TYPE",
    factorSource: "FACTOR_SOURCE",
    fuelType: "FUEL_TYPE",
    facilityType: "FACILITY_TYPE",
    auditEntity: "AUDIT_ENTITY",
    projectType: "PROJECT_TYPE",
    auditAction: "AUDIT_ACTION",
    complianceType: "COMPLIANCE_TYPE",
    complianceMethodology: "COMPLIANCE_METHODOLOGY",
    complianceScenario: "COMPLIANCE_SCENARIO",
    approvalStep: "APPROVAL_STEP",
    reportContentType: "REPORT_CONTENT_TYPE",
    inventoryReportType: "INVENTORY_REPORT_TYPE",
    gwpVersion: "GWP_VERSION",
    collectSchedule: "COLLECT_CYCLE",
    periodStat: "PERIOD_STAT",
    complianceStat: "COMPLIANCE_STAT",
    flexType: "FLEX_TYPE",
    flexLedgerStat: "FLEX_LEDGER_STAT",
    complianceStatus: "COMPLIANCE_STATUS",
    collStat: "COLL_STAT",
    calcStat: "CALC_STAT",
    execStat: "EXEC_STAT",
    reportStat: "REPORT_STAT",
    transStat: "TRANS_STAT",
    targetType: "TARGET_TYPE",
    accessLevel: "ACCESS_LEVEL",
    applicability: "APPLICABILITY",
    sourceType: "SOURCE_TYPE",
    dataQuality: "DATA_QUALITY",
    severity: "SEVERITY",
    emailStatus: "EMAIL_STATUS",
    reportArtifactType: "REPORT_ARTIFACT_TYPE",
    country: "REGION", // FactorMain 국가/출처용 (백엔드 REGION 그룹 추가 필요)
    eventType: "NOTIFICATION_EVENT",
    permissionAction: "PERMISSION_ACTION",
    activityUnit: "ACTIVITY_UNIT", // 260308: 배출계수/활동설정/활동데이터 단위 공통코드
    tier: "TIER_TYPE", // 산정등급 (TIER1~TIER4)
    calorificUnit: "CALORIFIC_UNIT", // 열량계수 단위 (순발열량 NCV, 총발열량 GCV) - 문서: 배출계수 체계화
    // 260311-REDES: EF/GWP Refactor
    gasCode: "GHG_SPECIES",
    horizon: "GWP_HORIZON",
    gasMassUnit: "MASS_UNIT",
    sourceRef: "GWP_SOURCE",
    profileScope: "PROFILE_SCOPE",
};

function createCategoryHelper(base, groupCode) {
    return {
        getLabel: (value) => base.getLabel(groupCode, value),
        getList: () => base.getList(groupCode),
        getLabelEn: (value) => base.getLabelEn(groupCode, value),
        getMeta: (value) => base.getMeta(groupCode, value),
    };
}

const CarbonCodesBase = {
    /** API에서 로드한 공통 코드 캐시 { [groupCode]: [{ value, text, textEn, meta }] } */
    _cache: null,

    /** 로드 완료 여부 */
    _loaded: false,

    /** 로드 중 Promise (중복 요청 방지) */
    _loadPromise: null,

    /**
     * API에서 공통 코드 로드 (Carbon Plugin 진입 시 1회)
     * @param {string} [useYn='Y'] - 사용 여부
     * @returns {Promise<void>}
     */
    async load(useYn = "Y") {
        if (this._loaded) {
            reactiveState.loaded = true;
            return;
        }
        if (this._loadPromise) return this._loadPromise;

        this._loadPromise = (async () => {
            try {
                const raw = await carbonApi.commonCode.getAllCodes(useYn);
                if (raw && typeof raw === "object") {
                    this._cache = raw;
                } else {
                    this._cache = {};
                }
            } catch (e) {
                console.warn("[CarbonCodes] API 로드 실패:", e?.message ?? e);
                this._cache = {};
            }
            // load 완료 → Vue 반응성 의존성 트리거
            reactiveState.loaded = true;
            // [임시] REGION(국가/출처) - API에 없을 경우 constants/codes REGIONS 사용 (백엔드 REGION 그룹 추가 후 제거)
            const regionList = this._cache?.REGION;
            if (!Array.isArray(regionList) || regionList.length === 0) {
                this._cache = this._cache ?? {};
            }
            this._loaded = true;
        })();

        return this._loadPromise;
    },

    /**
     * 특정 그룹의 코드 목록 조회
     * @param {string} groupCode - 그룹 코드 (예: FUEL_TYPE, PERIOD_STAT, INVENTORY_STAT)
     * @returns {Array<{value: string, text: string, textEn?: string, meta?: object}>}
     */
    getList(groupCode) {
        // getList 호출 시점에 "loaded"를 읽어 Vue 의존성을 등록 (로딩 완료 후 재렌더 유도)
        reactiveState.loaded;
        const list = this._cache?.[groupCode];
        return Array.isArray(list) ? list : [];
    },

    /**
     * 코드 값 → 한글 라벨 조회
     * @param {string} groupCode - 그룹 코드
     * @param {string} value - 코드 값
     * @returns {string}
     */
    getLabel(groupCode, value) {
        // getLabel 호출 시점에 "loaded"를 읽어 Vue 의존성을 등록 (로딩 완료 후 재렌더 유도)
        reactiveState.loaded;
        const list = this.getList(groupCode);
        const item = list.find((c) => c.value === value);
        return item?.text ?? value ?? "-";
    },

    /**
     * 코드 값 → 영문 라벨 조회
     * @param {string} groupCode - 그룹 코드
     * @param {string} value - 코드 값
     * @returns {string}
     */
    getLabelEn(groupCode, value) {
        // getLabelEn 호출 시점에 "loaded"를 읽어 Vue 의존성을 등록 (로딩 완료 후 재렌더 유도)
        reactiveState.loaded;
        const list = this.getList(groupCode);
        const item = list.find((c) => c.value === value);
        return item?.textEn ?? item?.text ?? value ?? "-";
    },

    /**
     * 코드 메타데이터 조회 (색상, 아이콘 등)
     * @param {string} groupCode - 그룹 코드
     * @param {string} value - 코드 값
     * @returns {object|undefined}
     */
    getMeta(groupCode, value) {
        // getMeta 호출 시점에 "loaded"를 읽어 Vue 의존성을 등록 (로딩 완료 후 재렌더 유도)
        reactiveState.loaded;
        const list = this.getList(groupCode);
        const item = list.find((c) => c.value === value);
        return item?.meta;
    },

    /**
     * 캐시 초기화 (테스트/재로드용)
     */
    reset() {
        this._cache = null;
        this._loaded = false;
        this._loadPromise = null;
        reactiveState.loaded = false;
    },
};

// 카테고리별 헬퍼: CarbonCodes.fuelType.getLabel(value), CarbonCodes.fuelType.getList() 등
for (const [field, groupCode] of Object.entries(FIELD_TO_GROUP)) {
    CarbonCodesBase[field] = createCategoryHelper(CarbonCodesBase, groupCode);
}

/**
 * 정의되지 않은 필드 접근 시 passThroughHelper 반환 → getLabel(value)가 value 그대로 반환
 * 예: CarbonCodes.facilityType.getLabel("office") → "office" (facilityType 미정의 시)
 */
const CarbonCodes = new Proxy(CarbonCodesBase, {
    get(target, prop) {
        if (prop in target) return target[prop];
        return passThroughHelper;
    },
});

export default CarbonCodes;
export { FIELD_TO_GROUP };
